import { ActivityModel, ActivityMediaFileModel } from "../Models";
import { ActionResponse, ActionStatusHelper, RecordMap, useAuditEnricher } from "@/Shared";
import { useMediaFile, useTask } from "@/OneLiners";

/**
 * =====================================================
 * Hook: useActivityEnricher
 * =====================================================
 *
 * Description:
 * Custom hook to enrich Activity domain data with all relevant relations.
 * Combines users, tasks, media files, and audit info into the ActivityModel.
 *
 * Responsibilities:
 * - Fetch and maintain related domain data (users, tasks, media files)
 * - Perform audit enrichment in O(1) using precomputed user RecordMap
 * - Track combined async status and errors
 * - Provide helper functions to enrich a single activity, by ID, or a full map
 *
 * Behavior:
 * - Resolves initiator and assigned agent users efficiently
 * - Links related task and media files
 * - Attaches audit fields (createdBy, updatedBy, etc.) using the shared audit hook
 * - Returns enriched data without unnecessary re-computation
 *
 * Notes:
 * - Compatible with both list and detail views of Activities
 * - Designed for performance at scale with large user/task/media datasets
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export function useActivityEnricher() {


     /** =====================================================
     * Fetch Related Data
     * =====================================================
     *
     * Hooks fetching related domain entities. Each exposes:
     * - dataMap: keyed by ID for O(1) lookups
     * - stateStatus: async loading state
     * - stateError: first encountered error
     */
    const { dataMap: mediaFileMap,  stateResponse : mediaFileStateResponse } = useMediaFile();

    const { dataMap: taskMap, stateResponse : taskStateResponse } = useTask();

    /**
     * Centralized audit enrichment hook
     * - Provides O(1) user resolution via precomputed RecordMap
     * - Exposes audit enrichment helpers and status/error
     */
    const {
        enricherResponse: auditEnricherResponse,
        userMap,
        getUserById,
        getEnrichedAudit,
    } = useAuditEnricher<ActivityModel>();


    /** =====================================================
     * Combined Loading & Error Status
     * =====================================================
     *
     * Combines status from all related hooks into a single ActionResponse:
     * - status: combined loading state (idle, loading, success, failed)
     * - error: first non-null error encountered
     */
    const enricherResponse: ActionResponse = {
        status: ActionStatusHelper.combine(
            taskStateResponse.status,
            auditEnricherResponse.status,
            mediaFileStateResponse.status
        ),
        error: taskStateResponse.error ??
            auditEnricherResponse.error ??
            mediaFileStateResponse.error
    };


     /** =====================================================
     * Public Helper Functions
     * =====================================================
     *
     * These functions enrich Activity entities with:
     * - initiator user
     * - assigned agent
     * - related task
     * - enriched media files (small & large)
     * - audit info (createdBy, updatedBy, deletedBy, restoredBy)
     */

    /** =====================================================
     * Enrich an entire map of activities
     * 
     * @param activityMap - RecordMap keyed by activity ID containing ActivityModel or undefined
     * @returns A new RecordMap with the same keys, each ActivityModel enriched with:
     *          - initiatorUser
     *          - assignedAgent
     *          - task
     *          - enriched activityMediaFiles
     *          - audit info (createdByUser, updatedByUser, etc.)
     * 
     * Notes:
     * - Preserves keys even if the value is undefined
     * - Uses O(1) lookups via RecordMaps for users, tasks, media files
     * ===================================================== */
    const getEnrichedActivityMap = (activityMap: RecordMap<ActivityModel>): RecordMap<ActivityModel> => {
       return activityMap.map<ActivityModel>(getStrictEnrichedActivity);
    }


    /** =====================================================
     * Enrich a single activity
     * 
     * @param activity - ActivityModel or undefined
     * @returns The ActivityModel enriched with:
     *          - initiatorUser
     *          - assignedAgent
     *          - task
     *          - enriched activityMediaFiles
     *          - audit info (createdByUser, updatedByUser, etc.)
     *          Returns undefined if input activity is undefined
     * 
     * Notes:
     * - Lookup of users, tasks, and media files is O(1)
     * - Handles missing media or task references gracefully
     * ===================================================== */
    const getEnrichedActivity = (activity?: ActivityModel): ActivityModel | undefined => {
        return activity ? getStrictEnrichedActivity(activity) : undefined;
    }

    const getStrictEnrichedActivity = (activity: ActivityModel) => {
        // Find related users
        const initiatorUser = getUserById(activity.initiatorId);
        const assignedAgent = getUserById(activity.assignedTo);

        // Find related task
        const task = taskMap?.get(activity.taskId);

        // Enrich media files
        const activityMediaFiles = [] as ActivityMediaFileModel[];
        (activity.activityMediaFiles ?? []).forEach(activityMediaFile => {

            if (activityMediaFile.smallMediaId) {
                const enrichedActivityMediaFile = { ...activityMediaFile };
                enrichedActivityMediaFile.smallMedia = mediaFileMap?.get(activityMediaFile.smallMediaId);
                activityMediaFiles.push(enrichedActivityMediaFile);
            }

            if (activityMediaFile.largeMediaId) {
                const enrichedActivityMediaFile = { ...activityMediaFile };
                enrichedActivityMediaFile.largeMedia = mediaFileMap?.get(activityMediaFile.largeMediaId);
                activityMediaFiles.push(enrichedActivityMediaFile);
            }
        });

        // Return enriched activity with audit info
        return {
            ...getEnrichedAudit(activity) as ActivityModel,
            initiatorUser,
            assignedAgent,
            activityMediaFiles,
            task
        }
    }

    /** =====================================================
     * Return API
     * =====================================================
     *
     * Exposes all enriched data and helper functions
     * for consuming components, fully typed and optimized.
     */
    return {
        enricherResponse,
        mediaFileMap,
        userMap,
        taskMap,
        getEnrichedActivity,
        getStrictEnrichedActivity,
        getEnrichedActivityMap
    }
}